import { useState } from 'react';
import { TableCell, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';
import { AppTable, LoaderBar, SearchInput, SkupremeModal } from '../../../components';
import { useDebouncedMemo, UsersListHook } from '../../../hooks';
import { CommonUtility, ToastMessage, WaveService } from '../../../utility';
import { HoverBorderButton, StyledButton } from '../../../elements';
import type { ITableColumn } from 'types/common/table';

const UsersContainer = styled.div`
  max-height: 50vh;
  margin-top: 10px;
  overflow-y: auto;
`;

// ---------------------------
// Interfaces
// ---------------------------
interface IUser {
  _id: string;
  firstName: string;
  lastName?: string;
  email: string;
  role: string;
}

interface IAssignWaveToUserPopupProps {
  onClose: () => void;
  open: boolean;
  waveId?: string;
  waveIds?: string[];
  refresh: () => void;
  resetCheck?: () => void;
}

interface IUserRowProps {
  item?: IUser;
  assignWave: (user: IUser) => Promise<void> | void;
}

// ---------------------------
// Main Component
// ---------------------------
export function AssignWaveToUserPopup({
  onClose,
  open,
  waveId,
  refresh,
  waveIds,
  resetCheck,
}: IAssignWaveToUserPopupProps) {
  const [search, setSearch] = useState<string>('');
  const { data: userData, loading: userLoading } = UsersListHook();
  const [loading, setLoading] = useState<boolean>(false);

  const columns: ITableColumn[] = [
    { text: 'Name', key: 'name', width: 3, textAlign: 'left' },
    { text: 'Email Address', key: 'EmailAddress', width: 4, textAlign: 'left' },
    { text: 'Role', key: 'role', width: 2, textAlign: 'left' },
    { text: '', width: 3, key: 'Actions', textAlign: 'right' },
  ];

  const filteredUsers = useDebouncedMemo<IUser[] | undefined>(
    () => {
      if (search && userData) {
        return userData.filter(
          (user: IUser) =>
            CommonUtility.searchQueryCompare(user.firstName, search) ||
            CommonUtility.searchQueryCompare(user.lastName, search) ||
            CommonUtility.searchQueryCompare(user.email, search),
        );
      }
      return userData;
    },
    [userData, search],
    100,
  );

  const assignWave = async (user: IUser) => {
    try {
      setLoading(true);
      if (CommonUtility.isValidArray(waveIds)) {
        await WaveService.assignWavesToUser({ waveIds, userId: user._id });
      } else {
        await WaveService.assignUser(waveId, { userId: user._id });
      }
      ToastMessage.success('Picker assigned successfully');
      refresh();
      resetCheck && resetCheck();
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SkupremeModal onClose={onClose} size="tiny" open={open} title="Assign Picker">
      {loading && <LoaderBar content="Assigning" />}
      <div className="d-flex justify-content-between align-items-center">
        <span>Please select a user to assign a wave</span>
        <SearchInput
          placeholder="Search Users"
          search={search}
          setSearch={(e: string) => setSearch(e)}
        />
      </div>
      <UsersContainer>
        <AppTable
          data={filteredUsers}
          RowComponent={<UserRow assignWave={assignWave} />}
          columns={columns}
          noDataMessage="No user data found"
          loading={userLoading}
          loadingRows={6}
          noShadow
        />
      </UsersContainer>
      <div className="mt-3">
        <HoverBorderButton onClick={onClose}>Close</HoverBorderButton>
      </div>
    </SkupremeModal>
  );
}

// ---------------------------
// Row Component
// ---------------------------
function UserRow({ item, assignWave }: IUserRowProps) {
  return (
    <TableRow>
      <TableCell>{item.firstName}</TableCell>
      <TableCell>{item.email}</TableCell>
      <TableCell>{item.role}</TableCell>
      <TableCell textAlign="right">
        <StyledButton onClick={() => assignWave(item)}>Assign</StyledButton>
      </TableCell>
    </TableRow>
  );
}
